/**
 * Tiny bilingual helper. All CMS text lives in the DB as { fr, en } objects;
 * `pick(bilingual, lang)` returns the right side and falls back gracefully.
 */
export function pick(value, lang = "fr") {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
        return value[lang] ?? value.fr ?? value.en ?? "";
    }
    return String(value);
}

/** Static UI strings that don't live in the CMS (nav, labels, form fields, etc.). */
export const UI_STRINGS = {
    nav: {
        home: { fr: "Accueil", en: "Home" },
        about: { fr: "À propos", en: "About" },
        practice: { fr: "Domaines", en: "Practice areas" },
        process: { fr: "Fonctionnement", en: "How it works" },
        blog: { fr: "Blog", en: "Blog" },
        contact: { fr: "Contact", en: "Contact" },
    },
    cta: {
        book: { fr: "Prendre rendez-vous", en: "Book a consultation" },
        consult_firm: { fr: "Consulter le cabinet", en: "Consult the firm" },
        read_more: { fr: "Lire l'article", en: "Read the article" },
        back_to_blog: { fr: "← Retour au blog", en: "← Back to the blog" },
        back_to_practice: {
            fr: "← Retour aux domaines",
            en: "← Back to practice areas",
        },
        send: { fr: "Envoyer", en: "Send" },
        sending: { fr: "Envoi en cours…", en: "Sending…" },
    },
    form: {
        name: { fr: "Nom complet", en: "Full name" },
        email: { fr: "Adresse courriel", en: "Email address" },
        phone: { fr: "Téléphone", en: "Phone" },
        subject: { fr: "Objet", en: "Subject" },
        domain: { fr: "Domaine concerné", en: "Practice area" },
        domain_placeholder: {
            fr: "— Sélectionner un domaine —",
            en: "— Select a practice area —",
        },
        message: {
            fr: "Votre message",
            en: "Your message",
        },
        consent: {
            fr: "J'accepte que mes informations soient utilisées uniquement pour répondre à ma demande. Elles restent couvertes par le secret professionnel.",
            en: "I agree that my information will be used solely to respond to my request. It remains covered by professional secrecy.",
        },
        required: { fr: "requis", en: "required" },
        success: {
            fr: "Votre message a bien été envoyé — le cabinet vous répondra dans les meilleurs délais.",
            en: "Your message has been sent — the firm will reply as soon as possible.",
        },
        error: {
            fr: "Une erreur est survenue. Merci de réessayer ou de nous contacter par téléphone.",
            en: "Something went wrong. Please try again or contact us by phone.",
        },
        consent_required: {
            fr: "Vous devez accepter la clause de confidentialité pour envoyer votre message.",
            en: "You must accept the confidentiality clause to send your message.",
        },
    },
    sections: {
        why_mabs: {
            fr: "Pourquoi le cabinet MABS",
            en: "Why the MABS firm",
        },
        practice_teaser: { fr: "Domaines de pratique", en: "Practice areas" },
        practice_teaser_sub: {
            fr: "Un accompagnement rigoureux dans sept domaines complémentaires.",
            en: "Rigorous support across seven complementary areas.",
        },
        recent_articles: { fr: "Derniers articles", en: "Recent articles" },
        first_consult: {
            fr: "Première consultation",
            en: "First consultation",
        },
        faq: { fr: "Questions fréquentes", en: "Frequently asked questions" },
        values: { fr: "Nos valeurs", en: "Our values" },
        situations_types: { fr: "Situations types", en: "Typical situations" },
        contact_quick: {
            fr: "Un message rapide au cabinet",
            en: "A quick note to the firm",
        },
        contact_quick_sub: {
            fr: "Pour une prise de contact simple. Réponse dans les meilleurs délais.",
            en: "For a simple first contact. We reply as soon as we can.",
        },
        opening_hours: { fr: "Horaires d'ouverture", en: "Opening hours" },
        find_us: { fr: "Nous trouver", en: "Find us" },
        map_note: {
            fr: "Bobo-Dioulasso, quartier Sarfalao.",
            en: "Bobo-Dioulasso, Sarfalao district.",
        },
    },
    misc: {
        member_of: {
            fr: "Membre du Barreau du Burkina Faso et du Barreau du Québec",
            en: "Member of the Bar of Burkina Faso and the Barreau du Québec",
        },
        dual_bar_short: {
            fr: "Barreau du Québec (2020) · Barreau du Burkina Faso (2022)",
            en: "Barreau du Québec (2020) · Bar of Burkina Faso (2022)",
        },
        legal_short: {
            fr: "Le contenu de ce site est fourni à titre informatif et ne constitue pas un avis juridique.",
            en: "The content of this website is provided for informational purposes only and does not constitute legal advice.",
        },
        legal_link: { fr: "Mentions légales", en: "Legal notice" },
        copyright: {
            fr: "© Cabinet d'avocats Maître Mina Adja Bintou Sanou",
            en: "© Cabinet d'avocats Maître Mina Adja Bintou Sanou Law Firm",
        },
        whatsapp_local: { fr: "WhatsApp Burkina", en: "WhatsApp Burkina" },
        whatsapp_canada: { fr: "WhatsApp Canada", en: "WhatsApp Canada" },
        write_to_us: { fr: "Écrire au cabinet", en: "Write to the firm" },
        loading: { fr: "Chargement…", en: "Loading…" },
        page_not_found: { fr: "Page introuvable", en: "Page not found" },
        page_not_found_body: {
            fr: "La page demandée n'existe pas ou a été déplacée.",
            en: "The page you requested does not exist or has been moved.",
        },
        go_home: { fr: "Retour à l'accueil", en: "Back to home" },
        cross_border_label: {
            fr: "Dossier Burkina–Canada",
            en: "Burkina–Canada matter",
        },
    },
};
